import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import ExercisePrompt from '../../../student/components/exercises/ExercisePrompt';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, ButtonBase, Typography } from '@mui/material';
import React from 'react';
import { QuizWordExercise, shows } from '../../../student/mockLessonContent';
import { BLUE, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SUCCESS_TEXT } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';
import { shuffledOrder } from './quizShuffle';

interface QuizWordExerciseCardProps {
    exercise: QuizWordExercise;
    onReady: ExerciseReady;
}

const WRONG_FLASH_MS = 650;

// "Quiz: Character Recognition" — a reading check: pick the right characters out
// of look-alikes. Hearing the answer and seeing pinyin are teacher options, off by
// default. A wrong pick reshuffles the choices; after two misses the student may skip.
export default function QuizWordExerciseCard({ exercise, onReady }: QuizWordExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const choices = React.useMemo(() => [exercise.answer, ...exercise.distractors], [exercise.answer, exercise.distractors]);
    const [round, setRound] = React.useState(0);
    const [order, setOrder] = React.useState(() => shuffledOrder(choices.length, exercise.id, 0));
    const [flashWrong, setFlashWrong] = React.useState<number | null>(null);
    const [misses, setMisses] = React.useState(0);
    const [solved, setSolved] = React.useState(false);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const handlePick = (index: number) => {
        if (solved || flashWrong != null) {
            return;
        }
        if (index === 0) {
            setSolved(true);
            onReady('done');
            return;
        }
        const nextMisses = misses + 1;
        setMisses(nextMisses);
        setFlashWrong(index);
        if (nextMisses === 2) {
            onReady('skip');
        }
        timeoutRef.current = window.setTimeout(() => {
            setFlashWrong(null);
            setRound(value => value + 1);
            setOrder(previous => shuffledOrder(choices.length, exercise.id, round + 1, previous));
        }, WRONG_FLASH_MS);
    };

    return (
        <Box>
            <ExerciseLabel>CHARACTER CHECK</ExerciseLabel>
            {exercise.teacher?.intro && <Box sx={{ mb: 2 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
            <ExercisePrompt text={exercise.prompt} />
            {shows(exercise, 'audio') && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <AudioPlayButton ariaLabel={`Play audio for ${exercise.answer.hanzi}`} size={56} />
                </Box>
            )}
            <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1.25 }}>
                {order.map(index => {
                    const choice = choices[index];
                    const isWrong = flashWrong === index;
                    const isCorrect = solved && index === 0;
                    return (
                        <ButtonBase
                            key={choice.hanzi}
                            onClick={() => handlePick(index)}
                            disabled={solved}
                            sx={{
                                minWidth: 0, minHeight: 60, p: 2, borderRadius: '16px',
                                textAlign: 'left', gap: 1.5,
                                backgroundColor: isCorrect ? `${GREEN}14` : isWrong ? `${RED}14` : '#fff',
                                border: `2px solid ${isCorrect ? GREEN : isWrong ? RED : 'transparent'}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                '&.Mui-focusVisible': { outline: `2px solid ${BLUE}`, outlineOffset: 3 },
                                opacity: solved && !isCorrect ? 0.5 : 1,
                                transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>
                                {choice.hanzi}
                            </Typography>
                            {shows(exercise, 'english') && choice.english && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>{choice.english}</Typography>
                            )}
                            {shows(exercise, 'pinyin') && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.5 }}>
                                    {choice.pinyin}
                                </Typography>
                            )}
                            </Box>
                            {isCorrect && <CheckCircleRoundedIcon sx={{ color: GREEN }} />}
                        </ButtonBase>
                    );
                })}
            </Box>
            {(solved || misses > 0) && (
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: solved ? SUCCESS_TEXT : ERROR_TEXT, mt: 2, textAlign: 'center' }}>
                    {solved
                        ? exercise.teacher?.correct ?? `对了! ${exercise.answer.hanzi}`
                        : `${exercise.teacher?.incorrect ?? 'Not that one.'} The choices have been shuffled — try again.`}
                </Typography>
            )}
            {!solved && misses >= 2 && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.75, textAlign: 'center' }}>
                    Still stuck? You can skip this one — Continue now reads “Skip”.
                </Typography>
            )}
            <ExerciseSupport exercise={exercise} />
        </Box>
    );
}
